import React, { useCallback, useEffect, useMemo } from 'react';
import { Pressable, StyleSheet, View, Platform } from 'react-native';
import { Image } from 'expo-image';
import PropTypes from 'prop-types';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useModelStore, useToolStore, useUIStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { trackEvent } from '@/constants/utils/analytics';
import { BORDER_RADIUS, MESSAGE_LAYOUT, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import useResponsiveness from '@/hooks/useResponsiveness';
import { getRecommendedImageTools } from '@/constants/utils/tool_intent_matcher';
import { getRecommendedMediaModels } from '@/constants/utils/media_model_suggestions';
import { isDesktopEditorSidebarType } from '@/constants/utils/files';

const createStyles = (colors, isXS, messageWidth) => StyleSheet.create({
  bubbleContainer: {
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
        : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
        : '100%',
    alignSelf: 'center',
    paddingHorizontal: isXS ? 16 : 0,
  },
  bubbleContainerInner: {
    flex: 1,
    width: '100%',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 20,
    backgroundColor: colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    gap: 14,
  },
  slimContainer: {
    paddingVertical: 8,
    paddingHorizontal: isXS ? 16 : 0,
    alignSelf: 'center',
    width: '100%',
    maxWidth:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
        : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
        : '100%',
    gap: 14,
  },
  wideContainer: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: isXS ? 16 : 0,
    alignSelf: 'center',
    width:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
        : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
        : '100%',
    maxWidth: '100%',
    gap: 14,
  },
  section: {
    gap: 10,
  },
  suggestionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: BORDER_RADIUS.round,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    maxWidth: '100%',
    flexShrink: 1,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      },
    }),
  },
  chipSecondary: {
    backgroundColor: 'transparent',
  },
  chipHovered: {
    backgroundColor: colors.solid_color_secondary,
    borderColor: colors.border_color_input,
  },
  chipDisabled: {
    opacity: 0.5,
    ...Platform.select({
      web: { cursor: 'not-allowed' },
    }),
  },
  chipLabel: {
    flexShrink: 1,
  },
  modelLogo: {
    width: 16,
    height: 16,
    borderRadius: 4,
  },
});

const MediaGenerationSuggestions = React.memo(({
  mediaContext,
  userQuery,
  onTryModel,
  canTryModels = true,
}) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const { t } = useTranslation();
  const messageLayout = useUIStore(state => state.messageLayout);
  const messageWidth = useUIStore(state => state.messageWidth);
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarType = useUIStore(state => state.contextSidebarType);

  const fetchCatalog = useToolStore(state => state.fetchCatalog);
  const isFetchedCatalog = useToolStore(state => state.isFetchedCatalog);
  const isLoadingCatalog = useToolStore(state => state.isLoadingCatalog);
  const toolsBySlug = useToolStore(state => state.toolsBySlug);

  const fetchModelsByTag = useModelStore(state => state.fetchModelsByTag);
  const getModelsByTag = useModelStore(state => state.getModelsByTag);
  const modelsByTag = useModelStore(state => state.modelsByTag);

  const modelTag = mediaContext?.modelTag || null;

  useEffect(() => {
    if (!isFetchedCatalog && !isLoadingCatalog) {
      fetchCatalog().catch(() => {});
    }
  }, [isFetchedCatalog, isLoadingCatalog, fetchCatalog]);

  useEffect(() => {
    if (!modelTag) return;
    fetchModelsByTag(modelTag).catch(() => {});
  }, [modelTag, fetchModelsByTag]);

  // Keep chips mounted even while a later turn is inferencing — canTryModels
  // only disables presses so a follow-up text reply doesn't make them vanish.
  const models = useMemo(() => {
    if (!mediaContext) return [];
    const catalog = getModelsByTag(mediaContext.modelTag) || [];
    return getRecommendedMediaModels(catalog, {
      excludeModelIds: mediaContext.usedModelIds,
      mediaKind: mediaContext.mediaKind,
      hasSourceFiles: Array.isArray(mediaContext.fileIds) && mediaContext.fileIds.length > 0,
    });
  }, [mediaContext, getModelsByTag, modelsByTag]);

  const tools = useMemo(() => {
    const query = userQuery || mediaContext?.prompt;
    if (!query) return [];
    // Keep tools secondary and capped when model chips are present.
    const recommended = getRecommendedImageTools(query, Object.values(toolsBySlug || {}));
    return models.length ? recommended.slice(0, 2) : recommended;
  }, [userQuery, mediaContext?.prompt, toolsBySlug, models.length]);

  const styles = useMemo(
    () => createStyles(colors, isXS, messageWidth),
    [colors, isXS, messageWidth]
  );

  const handleModelPress = useCallback((model) => {
    if (!mediaContext || !onTryModel || !canTryModels) return;
    trackEvent('chat', 'media_model_suggestion_clicked', model.slug || model.id);
    onTryModel({
      modelId: model.id,
      model,
      prompt: mediaContext.prompt,
      action: mediaContext.action,
      mediaKind: mediaContext.mediaKind,
      fileIds: mediaContext.fileIds || [],
    });
  }, [mediaContext, onTryModel, canTryModels]);

  const handleToolPress = useCallback((tool) => {
    trackEvent('tools', 'response_image_tool_suggestion_clicked', tool.slug);
    router.push(`/tools/${tool.slug}`);
  }, [router]);

  // Keep chat clear of upsells while a document / sheet / deck editor is open.
  if (isContextSidebarVisible && isDesktopEditorSidebarType(contextSidebarType)) return null;
  if (!models.length && !tools.length) return null;

  const content = (
    <>
      {models.length > 0 && (
        <View style={styles.section}>
          <View style={styles.suggestionHeader}>
            <Icon name="arrow-rotate-right" size={14} color={colors.text_color_primary} />
            <Text color="primary" size="m" weight="medium">
              {t('messages.mediaModelSuggestion.title')}
            </Text>
          </View>
          <View style={styles.chipsRow} accessibilityRole="list">
            {models.map((model) => (
              <Pressable
                key={model.id}
                style={({ hovered, pressed }) => [
                  styles.chip,
                  !canTryModels && styles.chipDisabled,
                  hovered && canTryModels && styles.chipHovered,
                  pressed && canTryModels && { opacity: 0.8 },
                ]}
                onPress={() => handleModelPress(model)}
                disabled={!canTryModels}
                accessibilityRole="button"
                accessibilityLabel={t('messages.mediaModelSuggestion.tryModel', { name: model.name })}
              >
                {model.logoPath ? (
                  <Image
                    source={{ uri: model.logoPath }}
                    style={styles.modelLogo}
                    contentFit="cover"
                  />
                ) : (
                  <Icon name="sparkles" size={12} color={colors.text_color_secondary} />
                )}
                <Text size="s" color="secondary" weight="semibold" numberOfLines={1} style={styles.chipLabel}>
                  {t('messages.mediaModelSuggestion.tryModel', { name: model.name })}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      )}

      {tools.length > 0 && (
        <View style={styles.section}>
          <View style={styles.suggestionHeader}>
            <Icon
              name="wand-magic-sparkles"
              size={models.length ? 12 : 14}
              color={models.length ? colors.text_color_tertiary : colors.text_color_primary}
            />
            <Text
              color={models.length ? 'tertiary' : 'primary'}
              size={models.length ? 's' : 'm'}
              weight="medium"
            >
              {models.length
                ? t('messages.mediaModelSuggestion.toolsSecondaryTitle')
                : t('messages.imageToolSuggestion.title')}
            </Text>
          </View>
          <View style={styles.chipsRow} accessibilityRole="list">
            {tools.map((tool) => (
              <Pressable
                key={tool.slug}
                style={({ hovered, pressed }) => [
                  styles.chip,
                  models.length > 0 && styles.chipSecondary,
                  hovered && styles.chipHovered,
                  pressed && { opacity: 0.8 },
                ]}
                onPress={() => handleToolPress(tool)}
                accessibilityRole="button"
                accessibilityLabel={t('home.tryTool', { name: tool.name })}
              >
                <Icon name="wand-magic-sparkles" size={12} color={colors.text_color_secondary} />
                <Text size="s" color="secondary" weight="semibold" numberOfLines={1} style={styles.chipLabel}>
                  {t('home.tryTool', { name: tool.name })}
                </Text>
                <Icon name="chevron-right" size={10} color={colors.text_color_tertiary} />
              </Pressable>
            ))}
          </View>
        </View>
      )}
    </>
  );

  if (messageLayout === MESSAGE_LAYOUT.SLIM) {
    return <View style={styles.slimContainer}>{content}</View>;
  }

  if (messageLayout === MESSAGE_LAYOUT.WIDE) {
    return <View style={styles.wideContainer}>{content}</View>;
  }

  return (
    <View style={styles.bubbleContainer}>
      <View style={styles.bubbleContainerInner}>
        {content}
      </View>
    </View>
  );
});

MediaGenerationSuggestions.propTypes = {
  mediaContext: PropTypes.shape({
    prompt: PropTypes.string.isRequired,
    mediaKind: PropTypes.oneOf(['image', 'video']).isRequired,
    usedModelIds: PropTypes.arrayOf(PropTypes.string),
    fileIds: PropTypes.arrayOf(PropTypes.string),
    action: PropTypes.string.isRequired,
    modelTag: PropTypes.string.isRequired,
  }),
  userQuery: PropTypes.string,
  onTryModel: PropTypes.func,
  canTryModels: PropTypes.bool,
};

MediaGenerationSuggestions.displayName = 'MediaGenerationSuggestions';

export default MediaGenerationSuggestions;
